import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Upload, Lock, Pencil, Download, CheckCircle2, XCircle } from "lucide-react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormService, FormField } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import FileUploadComponent from "@shared/components/FileUploadComponent";
import {
  useGlSources,
  useDownloadCsvTemplate,
  useTransactionCategoriesMultiple,
  useImportCsvGlCodes,
} from "../../hooks";
import { useWatch } from "react-hook-form";
import {
  RadialBarChart,
  RadialBar,
  ResponsiveContainer,
  PolarAngleAxis,
} from "recharts";

interface BulkImportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportSuccess?: (file: File, result?: any) => void;
}

export const BulkImportModal: React.FC<BulkImportModalProps> = ({
  open,
  onOpenChange,
  onImportSuccess,
}) => {
  const [sourceParam, setSourceParam] = React.useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = React.useState<number>(0);
  const [uploadState, setUploadState] = React.useState<
    "idle" | "uploading" | "error"
  >("idle");
  const [uploadResult, setUploadResult] = React.useState<any>(null);
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);

  const { data: sourcesData } = useGlSources();
  const { data: transactionCategoriesData } =
    useTransactionCategoriesMultiple(sourceParam);

  const sourcesOptions = React.useMemo(
    () =>
      (sourcesData || []).map((choice: any) => ({
        value: choice.value,
        label: choice.label,
      })),
    [sourcesData],
  );

  const transactionCategoriesOptions = React.useMemo(
    () =>
      (transactionCategoriesData || []).map((choice: any) => ({
        value: choice.value,
        label: choice.label,
      })),
    [transactionCategoriesData],
  );

  const step1Fields: FormField[] = [
    {
      name: "source",
      label: "Source",
      type: "multi-select",
      required: false,
      placeholder: "Select Source",
      options: sourcesOptions,
    },
    {
      name: "transactionCategory",
      label: "Transaction Category",
      type: "multi-select",
      required: false,
      placeholder: "Select Source first",
      disabled: !sourceParam.length,
      options: transactionCategoriesOptions,
    },
  ];

  const { mutateAsync: downloadTemplate, isPending: isDownloading } =
    useDownloadCsvTemplate();
  const { mutateAsync: importCsv } = useImportCsvGlCodes();

  const handleAutoUpload = async (file: File) => {
    setSelectedFile(file);
    setUploadState("uploading");
    setUploadProgress(0);
    setUploadResult(null);

    try {
      const result = await importCsv({
        file,
        action: "preview",
        onProgress: (percent) => setUploadProgress(percent),
      });
      setUploadProgress(100);
      onImportSuccess?.(file, result);
    } catch (err: any) {
      setUploadState("error");
      setUploadResult(err?.response?.data || { message: "Upload failed" });
    }
  };

  const step3Fields: FormField[] = [
    {
      name: "upload",
      type: "custom",
      fullWidth: true,
      customRender: (field, form) => (
        <div>
          {/* Hide the upload input once uploading/done/error */}
          {uploadState === "idle" && (
            <FileUploadComponent
              name={field.name}
              form={form}
              field={field}
              accept=".csv"
              placeholder="Click to browse or drag & drop"
              helperText=".csv files only"
              onChange={(files) => {
                if (files && files.length > 0) {
                  handleAutoUpload(files[0]);
                }
              }}
            />
          )}

          {/* Uploading progress inside the upload area */}
          {uploadState === "uploading" && (
            <div className="border-2 border-dashed border-[#1a73e8] rounded-xl p-6 bg-[#f8fbff] mt-2">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 flex-shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadialBarChart
                      cx="50%"
                      cy="50%"
                      innerRadius="65%"
                      outerRadius="100%"
                      data={[{ value: uploadProgress }]}
                      startAngle={90}
                      endAngle={-270}
                    >
                      <PolarAngleAxis
                        type="number"
                        domain={[0, 100]}
                        angleAxisId={0}
                        tick={false}
                      />
                      <RadialBar
                        background={{ fill: "#e8f0fe" }}
                        dataKey="value"
                        angleAxisId={0}
                        fill="#1a73e8"
                        cornerRadius={8}
                      />
                      <text
                        x="50%"
                        y="50%"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{ fontSize: 13, fontWeight: 700, fill: "#1e293b" }}
                      >
                        {uploadProgress}%
                      </text>
                    </RadialBarChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1">
                  <p className="text-gray-800 font-semibold text-sm">Uploading...</p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{selectedFile?.name}</p>
                  <div className="mt-3 h-1.5 bg-[#e8f0fe] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#1a73e8] rounded-full transition-all duration-200"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ),
    },
  ];

  const form = FormService.createForm({
    fields: [...step1Fields, ...step3Fields],
    mode: "onChange",
  });

  const handleFieldChange = (name: string, value: any[]) => {
    switch (name) {
      case "source":
        form.setValue("transactionCategory", []);
        setSourceParam(value?.length ? value : []);
        break;
      default:
        break;
    }
  };

  const selectedSources: any[] =
    useWatch({ control: form.control, name: "source" }) || [];
  const selectedCategories: any[] =
    useWatch({ control: form.control, name: "transactionCategory" }) || [];

  const handleDownload = () => {
    downloadTemplate({
      source: selectedSources.length ? selectedSources.join(",") : undefined,
      transactionCategory: selectedCategories.length
        ? selectedCategories.join(",")
        : undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl gap-0 p-0 overflow-hidden bg-white">
        <DialogHeader className="p-6 bg-white">
          {" "}
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-[#f0f7ff] text-[#1a73e8]">
              <Upload className="w-5 h-5" />
            </div>
            <div className="text-left">
              <DialogTitle className="text-xl font-bold text-gray-900">
                Bulk Import GL Codes
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-1">
                Download a pre-filled template, add your GL Code & Name, then upload
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className="p-6 max-h-[75vh] overflow-y-auto space-y-6">
          {/* Info Section */}
          <div className="bg-[#f8fbff] rounded-xl p-5 border border-[#e5f0ff]">
            <h3 className="text-[#1a73e8] font-bold mb-2">
              Only 2 fields required from you
            </h3>
            <p className="text-sm text-gray-600 mb-4 leading-relaxed">
              The template is pre-filled with{" "}
              <strong className="text-[#1a73e8]">Source</strong>,{" "}
              <strong className="text-[#1a73e8]">Transaction Category</strong>,
              and <strong className="text-[#1a73e8]">Transaction Type</strong>.
              You only need to enter your{" "}
              <strong className="text-[#1a73e8]">GL Code</strong> and{" "}
              <strong className="text-[#1a73e8]">GL Name</strong>.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#111827] text-white text-xs font-medium">
                <Pencil className="w-3 h-3" /> GL Code{" "}
                <span className="text-gray-400 font-normal">← fill this</span>
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#111827] text-white text-xs font-medium">
                <Pencil className="w-3 h-3" /> GL Name{" "}
                <span className="text-gray-400 font-normal">← fill this</span>
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-gray-600 text-xs font-medium border border-gray-200 shadow-sm">
                <Lock className="w-3 h-3 text-gray-400" /> GL Type
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-gray-600 text-xs font-medium border border-gray-200 shadow-sm">
                <Lock className="w-3 h-3 text-gray-400" /> Source
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-gray-600 text-xs font-medium border border-gray-200 shadow-sm">
                <Lock className="w-3 h-3 text-gray-400" /> Transaction Category
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-gray-600 text-xs font-medium border border-gray-200 shadow-sm">
                <Lock className="w-3 h-3 text-gray-400" /> Transaction Type
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-gray-600 text-xs font-medium border border-gray-200 shadow-sm">
                <Lock className="w-3 h-3 text-gray-400" /> Status
              </span>
            </div>
          </div>

          {/* Step 1 */}
          <div className="border border-gray-200 rounded-xl bg-white relative">
            <div className="bg-[#f8f9fa] border-b border-gray-200 px-5 py-4 flex items-center justify-between rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#1a73e8] text-white font-bold text-sm">
                  1
                </div>
                <h3 className="text-gray-900 font-bold text-base">
                  Customise your template
                </h3>
              </div>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 mb-4">
                Filter the template rows by{" "}
                <strong className="text-gray-700">Source</strong> and/or{" "}
                <strong className="text-gray-700">Transaction Category</strong>.
                Leave both empty to include all rows.
              </p>
              <div className="mb-5">
                <DynamicForm
                  fields={step1Fields}
                  form={form}
                  onSubmit={() => {}}
                  onFieldChange={handleFieldChange}
                  config={{ showSubmitButton: false, showResetButton: false }}
                  gridColumns={2}
                  className="w-full"
                />
              </div>
              <Button
                variant="outline"
                className="bg-[#f0f7ff] text-[#1a73e8] border-[#c2e0ff] hover:bg-[#e6f2ff]"
                onClick={handleDownload}
                disabled={isDownloading}
              >
                <Download className="w-4 h-4 mr-2" />
                {isDownloading ? "Downloading..." : "Download Template"}
              </Button>
            </div>
          </div>

          {/* Step 2 */}
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-1">
              <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#1a73e8] text-white font-bold text-sm">
                2
              </div>
            </div>
            <div className="flex-1 border border-gray-200 rounded-xl p-5 bg-[#f8f9fa]">
              <h3 className="text-gray-900 font-bold text-base mb-1.5">
                Fill in GL Code & GL Name
              </h3>
              <p className="text-sm text-gray-600">
                Open the file in Excel or Google Sheets. Replace{" "}
                <code className="bg-gray-200 px-1.5 py-0.5 rounded text-gray-700 font-mono text-xs">
                  FILL_CODE
                </code>{" "}
                with your GL code and{" "}
                <code className="bg-gray-200 px-1.5 py-0.5 rounded text-gray-700 font-mono text-xs">
                  FILL_NAME
                </code>{" "}
                with the GL name. Delete any rows you don't need.
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-1">
              <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#1a73e8] text-white font-bold text-sm">
                3
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-gray-900 font-bold text-base mb-4 mt-0.5">
                Upload your completed file
              </h3>
              {uploadState !== "error" && (
                <DynamicForm
                  fields={step3Fields}
                  form={form}
                  onSubmit={() => {}}
                  config={{ showSubmitButton: false, showResetButton: false }}
                  gridColumns={1}
                  className="w-full"
                />
              )}

              {/* Error state */}
              {uploadState === "error" && (
                <div className="mt-2 border border-red-200 rounded-xl p-5 bg-red-50">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-red-100 rounded-lg">
                      <XCircle className="w-6 h-6 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-red-800 font-bold text-base">
                        Import Failed
                      </p>
                      <p className="text-sm text-red-700 mt-1">
                        File: <span className="font-semibold">{selectedFile?.name}</span>
                      </p>
                      <p className="text-sm text-red-700 mt-1">
                        Error: {uploadResult?.message || uploadResult?.detail || "An error occurred during import."}
                      </p>
                      <div className="flex items-center gap-3 mt-4">
                        <Button
                          size="sm"
                          className="bg-red-600 hover:bg-red-700 text-white"
                          onClick={() => selectedFile && handleAutoUpload(selectedFile)}
                        >
                          Retry Upload
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-100"
                          onClick={() => {
                            setSelectedFile(null);
                            setUploadState("idle");
                            setUploadProgress(0);
                          }}
                        >
                          Try Another File
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
